const STEPS = [
  { num: 1, icon: "bi-radar", color: "#0B8F7A", title: "Discover", text: "Find personal data with agents or agentless scans across systems and files." },
  { num: 2, icon: "bi-tags", color: "#2B6CB0", title: "Classify", text: "Label structured and unstructured data so sensitivity and purpose are clear." },
  { num: 3, icon: "bi-map", color: "#6B46C1", title: "Map & Assess", text: "Build ROPA, run Gap Assessments and PIAs on real processing activity." },
  { num: 4, icon: "bi-people", color: "#B8862E", title: "Operate rights", text: "Manage Consent, DSAR and third-party risk from one unified workspace." },
  { num: 5, icon: "bi-stars", color: "#C2410C", title: "AI policies", text: "Generate, review and publish policies in the Policy Store, linked to controls." },
  { num: 6, icon: "bi-shield-check", color: "#15803D", title: "Govern & prove", text: "Monitor risk, respond to breaches and stay audit-ready with a living trail." },
];

function Step({ num, icon, color, title, text }) {
  return (
    <div className="text-left sm:text-center px-1 grid sm:block grid-cols-[auto_1fr] gap-4 items-start sm:items-stretch">
      <div className="group">
        <div
          className="relative w-[68px] h-[68px] sm:w-[88px] sm:h-[88px] mx-0 sm:mx-auto mb-0 sm:mb-[1.15rem] rounded-full flex items-center justify-center bg-white border-2 shadow-[0_10px_28px_rgba(7,17,31,0.06)] transition-all duration-[250ms] group-hover:-translate-y-1 group-hover:scale-[1.04]"
          style={{ borderColor: color }}
        >
          <i className="text-[1.25rem] sm:text-[1.55rem]" style={{ color }}>
            <i className={`bi ${icon}`} />
          </i>
          <span
            className="absolute -top-[6px] -right-1 w-[26px] h-[26px] rounded-full font-bricolage text-[0.72rem] font-bold flex items-center justify-center text-white border-2 border-white"
            style={{ background: color }}
          >
            {num}
          </span>
        </div>
      </div>
      <div>
        <h3 className="text-[1rem] font-bricolage font-semibold mb-[0.4rem] text-ink">{title}</h3>
        <p className="m-0 text-[0.84rem] leading-[1.5] text-slate">{text}</p>
      </div>
    </div>
  );
}

export default function HowItWorks() {
  return (
    <section id="how" className="py-[5.5rem] bg-white border-b border-line overflow-hidden">
      <div className="container mx-auto max-w-[1140px] px-4">
        <p className="inline-block text-[0.78rem] font-semibold tracking-[0.12em] uppercase text-teal mb-[0.85rem]">
          How OneDPDP works
        </p>
        <h2 className="font-bricolage text-[clamp(1.85rem,3.2vw,2.45rem)] mb-3 text-ink">
          A clear path from data to compliance
        </h2>
        <p className="text-[1.08rem] leading-[1.65] text-slate max-w-[40rem]">
          Six connected steps take you from finding personal data to proving
          you protect it — so every team understands the journey, not just
          the tool names.
        </p>

        <div className="relative mt-[3.25rem]">
          {/* connecting track - desktop only */}
          <div
            className="hidden lg:block absolute top-[44px] left-[8%] right-[8%] h-[3px] rounded-[3px] opacity-[0.35] overflow-hidden"
            style={{
              background: "linear-gradient(90deg, #0B8F7A, #2B6CB0, #6B46C1, #B8862E, #C2410C, #15803D)",
            }}
            aria-hidden="true"
          >
            <div
              className="absolute inset-0 animate-flow-shimmer motion-safe-anim"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
              }}
            />
          </div>

          <div className="relative z-[1] grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-7">
            {STEPS.map((s) => (
              <Step key={s.num} {...s} />
            ))}
          </div>
        </div>

        <div
          className="mt-12 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 items-center p-[1.5rem_1.75rem] rounded-xl border border-line text-left"
          style={{
            background: "linear-gradient(135deg, rgba(11, 143, 122, 0.06), rgba(201, 164, 92, 0.08))",
          }}
        >
          <div className="w-14 h-14 rounded-[14px] flex items-center justify-center text-[1.45rem] bg-[#E6F7F3] text-[#0B8F7A]">
            <i className="bi bi-arrow-repeat" />
          </div>
          <div>
            <h3 className="text-[1.15rem] font-bricolage font-semibold mb-[0.3rem]">
              Continuous loop — not a one-time project
            </h3>
            <p className="m-0 text-slate text-[0.95rem] leading-[1.55]">
              Every new system, vendor or process flows back through
              discovery and assessment. OneDPDP keeps your DPDP programme
              current as the business changes — so compliance stays
              operational, not ceremonial.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
